/**
 * WorkspaceLogoUpload - Croppie-based dual-mode workspace asset uploader.
 *
 * Ported from `unicorn-webapp/src/components/common/ui/AvatarUpload.tsx`.
 * Two modes:
 *   - `mode="logo"` - wide / free aspect (default 3:1 viewport, 1200×400 output).
 *   - `mode="icon"` - square 1:1, 512×512 output.
 *
 * Storage: posts to the `offline-session` worker at
 *   `${VITE_OFFLINE_SESSION_WORKER_URL}/workspace/:id/asset?kind=logo|icon`
 * which writes to the (private) Cloudflare R2 `WORKSPACE_ASSETS` bucket and
 * patches `brands.logo_url` / `brands.icon_url` with the **R2 object key**.
 * Bytes are served back to the browser by the Pages Function
 * `functions/api/workspace-assets/[workspaceId]/[kind].ts` (mirroring the
 * `interview-videos` pattern). The worker response carries the relative URL
 * `/api/workspace-assets/{id}/{kind}` so this component can hand it to a
 * caller as `<img src>` directly. The frontend never touches Supabase Storage
 * for workspace assets.
 *
 * Validation (mirrors server):
 *   - Allowed MIME: image/png, image/jpeg, image/webp.
 *   - SVG is rejected client-side (no sanitiser yet - Phase A).
 *   - Size cap: 5 MB logo, 2 MB icon.
 *
 * Errors are captured to PostHog with context `WorkspaceLogoUpload.handler`
 * and `{ workspaceId, kind }` properties.
 *
 * @module components/workspace/WorkspaceLogoUpload
 */

import React, { useEffect, useRef, useState } from 'react';
import Croppie from 'croppie';
import 'croppie/croppie.css';
import { Modal } from '../ui/Modal';
import { ButtonContent } from '../ui/ButtonContent';
import { useSupabase } from '../../lib/SupabaseProvider';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import { offlineSessionWorkerUrl } from '../../lib/worker-urls';
import { PlusIcon } from '@/components/icons';
import { clsx } from 'clsx';

/** Upload mode - wide free-aspect or square. */
export type WorkspaceLogoUploadMode = 'logo' | 'icon';

interface WorkspaceLogoUploadProps {
  /** Workspace id - used in the worker URL and in PostHog properties. */
  workspaceId: string;
  /** Which slot this instance owns. */
  kind: WorkspaceLogoUploadMode;
  /** Current relative URL for the slot (renders as the preview). Built by
   * the parent via `workspaceAssetUrl(workspace, kind)`. Null when the
   * workspace has no asset of this kind. */
  currentUrl: string | null;
  /** Called with the new relative URL after a successful upload. The worker
   * returns `/api/workspace-assets/{id}/{kind}` which the parent can plug
   * straight into state. */
  onUploaded: (url: string) => void;
  /** Optional label rendered above the preview. */
  label?: string;
  /** Optional helper text rendered below the preview. */
  helperText?: string;
}

const ALLOWED_MIMES = ['image/png', 'image/jpeg', 'image/webp'] as const;
/**
 * MIME types accepted by the file `<input>` itself. We deliberately include
 * `image/svg+xml` so the OS picker shows SVG files - clicking one then
 * surfaces our explicit "SVG uploads are not supported yet" error rather
 * than silently filtering the file out (which userEvent + many browsers do
 * when the type is missing from `accept`).
 */
const FILE_INPUT_ACCEPT = [...ALLOWED_MIMES, 'image/svg+xml'].join(',');
const MAX_BYTES: Record<WorkspaceLogoUploadMode, number> = {
  logo: 5 * 1024 * 1024,
  icon: 2 * 1024 * 1024,
};
const OUTPUT: Record<WorkspaceLogoUploadMode, { width: number; height: number }> = {
  logo: { width: 1200, height: 400 },
  icon: { width: 512, height: 512 },
};

/**
 * Resize a Blob to fit inside (maxWidth, maxHeight) using a canvas. Mirrors
 * the AvatarUpload reference helper. Used after Croppie produces the cropped
 * blob so we always upload a deterministic size.
 *
 * Resolves with the original blob (best-effort fallback) if the image fails
 * to load OR a 250 ms timeout elapses - guarantees the function never hangs
 * the caller in environments where blob-URL image loading is unsupported
 * (jsdom, headless tests).
 */
function resizeImage(
  blob: Blob,
  maxWidth: number,
  maxHeight: number,
  quality = 0.9,
): Promise<Blob> {
  return new Promise((resolve) => {
    const img = new Image();
    let settled = false;
    const url = URL.createObjectURL(blob);
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      try {
        URL.revokeObjectURL(url);
      } catch {
        // ignore
      }
      resolve(blob);
    }, 250);

    img.onload = () => {
      if (settled) return;
      try {
        URL.revokeObjectURL(url);
      } catch {
        // ignore
      }
      let { width, height } = img;
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width *= ratio;
        height *= ratio;
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        settled = true;
        clearTimeout(timer);
        resolve(blob);
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (resized) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(resized ?? blob);
        },
        'image/png',
        quality,
      );
    };
    img.onerror = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try {
        URL.revokeObjectURL(url);
      } catch {
        // ignore
      }
      resolve(blob);
    };
    img.src = url;
  });
}

/**
 * Dual-mode workspace asset uploader. Renders a clickable preview that opens
 * a Croppie modal on file select. After the user crops + saves, the result
 * is uploaded to the offline-session worker and `onUploaded(url)` fires.
 */
export const WorkspaceLogoUpload: React.FC<WorkspaceLogoUploadProps> = ({
  workspaceId,
  kind,
  currentUrl,
  onUploaded,
  label,
  helperText,
}) => {
  const { supabase } = useSupabase();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const croppieRef = useRef<Croppie | null>(null);

  const [previewUrl, setPreviewUrl] = useState<string | null>(currentUrl);
  const [selectedSrc, setSelectedSrc] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    setPreviewUrl(currentUrl);
  }, [currentUrl]);

  // Bind Croppie when the modal opens with a source image.
  useEffect(() => {
    if (!showModal || !selectedSrc) return undefined;
    // The Modal portals through Headless UI; ref attachment can race with
    // effect scheduling in some test environments. Defer one microtask so
    // the ref is guaranteed populated before we instantiate Croppie.
    let cancelled = false;
    void Promise.resolve().then(() => {
      if (cancelled) return;
      const node = containerRef.current;
      if (!node) return;
      if (croppieRef.current) {
        try {
          croppieRef.current.destroy();
        } catch {
          // Croppie destroy can throw if container is already gone - ignore.
        }
      }
      const boundary = node.offsetWidth || 320;
      const viewportWidth = Math.floor(boundary * 0.85);
      const viewport =
        kind === 'icon'
          ? { width: viewportWidth, height: viewportWidth, type: 'square' as const }
          : { width: viewportWidth, height: Math.floor(viewportWidth / 3), type: 'square' as const };
      croppieRef.current = new Croppie(node, {
        enableExif: true,
        enableOrientation: true,
        enableZoom: true,
        showZoomer: true,
        // Allow the image to shrink smaller than the viewport. Odd-shaped
        // logos (very wide, very tall, transparent margins) hit Croppie's
        // default min-zoom - which keeps the image >= viewport - before
        // they fit; with the boundary off the user can zoom out further
        // and the cropped output keeps the empty space as transparent PNG.
        enforceBoundary: false,
        boundary: { width: boundary, height: boundary },
        viewport,
      });
      void croppieRef.current.bind({ url: selectedSrc });
    });
    return () => {
      cancelled = true;
      try {
        croppieRef.current?.destroy();
      } catch {
        // ignore
      }
      croppieRef.current = null;
    };
  }, [showModal, selectedSrc, kind]);

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;
    setValidationError(null);

    if (file.type === 'image/svg+xml') {
      setValidationError('SVG uploads are not supported yet. Use PNG, JPEG, or WEBP.');
      return;
    }
    if (!ALLOWED_MIMES.includes(file.type as (typeof ALLOWED_MIMES)[number])) {
      setValidationError(`Unsupported file type: ${file.type || 'unknown'}.`);
      return;
    }
    if (file.size > MAX_BYTES[kind]) {
      const limit = kind === 'logo' ? '5MB' : '2MB';
      setValidationError(`File must be under ${limit}.`);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        setSelectedSrc(reader.result);
        setShowModal(true);
      }
    };
    reader.onerror = () => setValidationError('Could not read file.');
    reader.readAsDataURL(file);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedSrc(null);
  };

  const handleSave = async () => {
    if (!croppieRef.current) return;
    setSubmitting(true);
    try {
      const blob = (await croppieRef.current.result({
        type: 'blob',
        format: 'png',
        size: OUTPUT[kind],
        circle: false,
      })) as unknown as Blob;
      // Croppie already honours `size` for the output, but we re-run a
      // canvas-based resize as defence-in-depth (matches the AvatarUpload
      // reference). `resizeImage` falls back to the original blob on any
      // failure path, so test environments without a real canvas/Image
      // pipeline still hit the upload path.
      const sized = await resizeImage(blob, OUTPUT[kind].width, OUTPUT[kind].height);

      const form = new FormData();
      form.append('file', new File([sized], `${kind}.png`, { type: 'image/png' }));

      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        throw new Error('Not authenticated');
      }

      const base = offlineSessionWorkerUrl();
      const endpoint = `${base}/workspace/${encodeURIComponent(workspaceId)}/asset?kind=${kind}`;
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`Upload failed (${res.status}): ${body.slice(0, 120)}`);
      }
      const json = (await res.json()) as { url?: string };
      const newUrl = json.url ?? null;
      if (!newUrl) {
        throw new Error('Upload response missing url');
      }
      // The worker returns a versioned URL (`/api/workspace-assets/{id}/{kind}?v={updated_at}`)
      // so the CDN edge can keep its long `max-age` cache and still miss on
      // re-uploads - the version bumps with the brand row's `updated_at`.
      // No client-side cache-busting needed; pass the URL through as-is.
      setPreviewUrl(newUrl);
      onUploaded(newUrl);
      closeModal();
    } catch (err) {
      captureException(
        err instanceof Error ? err : new Error(String(err)),
        'WorkspaceLogoUpload.handler',
        { workspaceId, kind },
      );
      setValidationError(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const aspectClass = kind === 'icon' ? 'aspect-square w-32' : 'aspect-[3/1] w-64';

  return (
    <div className="flex flex-col gap-2">
      {label && (
        <span className="text-sm font-medium text-muted-foreground">{label}</span>
      )}

      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        className={clsx(
          'relative flex items-center justify-center overflow-hidden cursor-pointer',
          'border-2 border-dashed border-border rounded-xl bg-background',
          'hover:border-volt/60 transition-colors',
          aspectClass,
        )}
        aria-label={`Upload ${kind}`}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={`${kind} preview`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="flex flex-col items-center text-muted-foreground">
            <PlusIcon className="w-6 h-6" />
            <span className="text-xs mt-1">Upload {kind}</span>
          </div>
        )}
      </button>

      <input
        ref={fileInputRef}
        type="file"
        data-testid="workspace-logo-file-input"
        accept={FILE_INPUT_ACCEPT}
        onChange={onPickFile}
        className="hidden"
      />

      {(helperText || validationError) && (
        <p
          className={clsx(
            'text-xs',
            validationError ? 'text-danger' : 'text-muted-foreground',
          )}
        >
          {validationError ?? helperText}
        </p>
      )}

      <Modal open={showModal} onClose={closeModal} title={`Crop ${kind}`} maxWidth="max-w-lg">
        <div ref={containerRef} className="w-full min-h-[280px]" />
        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={closeModal}
            disabled={submitting}
            className="rounded-lg px-4 py-2.5 border border-border text-foreground font-medium hover:bg-surface-hover transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={submitting}
            aria-busy={submitting}
            className="rounded-lg px-4 py-2.5 button-accent font-medium transition-colors disabled:opacity-50"
          >
            <ButtonContent loading={submitting}>Save</ButtonContent>
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default WorkspaceLogoUpload;
